import React, {
  useEffect,
  useState,
  useCallback,
  useMemo,
  useRef,
} from "react";
import {
  Modal,
  Button,
  Row,
  Col,
  Card,
  Badge,
  ListGroup,
  Form,
} from "react-bootstrap";
import CheckIcon from "@mui/icons-material/Check";
import ClearIcon from "@mui/icons-material/Clear";
import PercentIcon from "@mui/icons-material/Percent";
import AssignmentTurnedInIcon from "@mui/icons-material/AssignmentTurnedIn";
import XLSX from "xlsx";

const prioridadTipoAlerta = (tipo = "") => {
  if (tipo === "Vulnerabilidades explotadas") return 0;
  if (tipo === "Vulnerabilidades con Exploit conocido") return 1;
  return 2;
};

const renderBoolIcon = (val) => {
  if (val === "SI") return <CheckIcon style={{ color: "#4CAF50" }} />;
  if (val === "NO") return <ClearIcon style={{ color: "#F44336" }} />;
  return <span title={val || "-"}>{val || "-"}</span>;
};

const colores = {
  0: "#F44336",
  33.33: "#FF9800",
  66.66: "#FFEB3B",
  100: "#4CAF50",
};

const closestBucket = (val) => {
  const keys = Object.keys(colores).map(Number);
  const n = Number(val);
  if (Number.isNaN(n)) return null;
  let best = keys[0];
  let bestDist = Math.abs(n - keys[0]);
  for (const k of keys.slice(1)) {
    const d = Math.abs(n - k);
    if (d < bestDist) {
      best = k;
      bestDist = d;
    }
  }
  return best;
};

const renderPorcentajeFijo = (val) => {
  const n = Number(val);
  if (!Number.isFinite(n)) return <span>-</span>;

  const bucket = colores[n] ? n : closestBucket(n);
  const color = colores[bucket] || "#9CA3AF";

  return (
    <span
      style={{ display: "inline-flex", alignItems: "center" }}
      title={`${n}%`}
    >
      <span style={{ color, fontWeight: "bold", marginRight: 4 }}>{n}</span>
      <PercentIcon style={{ color, fontSize: 20 }} />
    </span>
  );
};

const ordenarPorOrigenIderoYTipo = (items = []) => {
  return [...items].sort((a, b) => {
    const origenA = a.__origen === "propias" ? 0 : 1;
    const origenB = b.__origen === "propias" ? 0 : 1;
    if (origenA !== origenB) return origenA - origenB;

    const ideroA = Number(a?.idero ?? Infinity);
    const ideroB = Number(b?.idero ?? Infinity);
    if (ideroA !== ideroB) return ideroA - ideroB;

    const pa = prioridadTipoAlerta(a?.tipo_alerta);
    const pb = prioridadTipoAlerta(b?.tipo_alerta);
    if (pa !== pb) return pa - pb;

    return Number(a?.idalertas ?? 0) - Number(b?.idalertas ?? 0);
  });
};

const BoolChip = ({ label, value }) => {
  const color =
    value === "SI" ? "#10B981" : value === "NO" ? "#EF4444" : "#6B7280";
  const Icon = value === "SI" ? CheckIcon : value === "NO" ? ClearIcon : null;

  return (
    <Badge
      style={{
        backgroundColor: color,
        color: "#fff",
        padding: "6px 10px",
        borderRadius: "999px",
        display: "inline-flex",
        alignItems: "center",
        gap: 6,
      }}
      title={`${label}: ${value ?? "-"}`}
    >
      {Icon && <Icon fontSize="small" />} {label}
    </Badge>
  );
};

const EfectosChip = ({ value }) => {
  const n = Number(value);
  const bucket = colores[n] ? n : closestBucket(n);
  const color = colores[bucket] || "#9CA3AF";
  return (
    <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
      <Badge
        style={{
          backgroundColor: color,
          color: "#000",
          fontWeight: "bold",
          fontSize: "0.85rem",
          padding: "8px 12px",
          borderRadius: "999px",
          display: "inline-flex",
          alignItems: "center",
          lineHeight: 1,
        }}
        title={`Efectos: ${Number.isFinite(n) ? `${n}%` : "-"}`}
      >
        {Number.isFinite(n) ? `Efectos: ${n}%` : "-"}
      </Badge>
    </span>
  );
};

const colWidths = {
  idevaluacion: "100px",
  tipoAlerta: "220px",
  analista: "200px",
  idActivo: "110px",
  nombre: "220px",
  elementos: "260px",
  control: "120px",
  vulnerabilidad: "140px",
  efectos: "110px",
  infraestructura: "160px",
  acciones: "100px",
};

const styles = {
  tableStyle: {
    width: "100%",
    borderCollapse: "collapse",
    tableLayout: "fixed",
  },
  tableHeaderStyle: {
    borderBottom: "1px solid #e5e7eb",
    textAlign: "left",
    padding: "8px 12px",
    background: "#f9fafb",
    color: "#374151",
    fontWeight: "bold",
    lineHeight: "1.25rem",
  },
  CellStyle: {
    borderBottom: "1px solid #e5e7eb",
    padding: "8px 12px",
    fontSize: "0.95rem",
    color: "#111827",
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
    lineHeight: "1.5rem",
    height: "40px",
  },
  button: {
    padding: "6px 10px",
    border: "1px solid #d1d5db",
    borderRadius: "6px",
    background: "#fff",
    cursor: "pointer",
  },
  tableWrapper: {
    maxHeight: "calc(56px + 40px * var(--rows))",
    overflowY: "auto",
    border: "1px solid #e5e7eb",
    borderRadius: "8px",
  },

  title: {
    fontSize: "2.25rem",
    fontWeight: "bold",
    color: "#1f2937",
    marginBottom: "8px",
  },
  container: {
    display: "flex",
    flexDirection: "column",
    width: "100%",
    minHeight: "100vh",
    background: "white",
    padding: "24px",
  },
  tableStyle: {
    width: "100%",
    borderCollapse: "collapse",
    backgroundColor: "white",
    borderRadius: "8px",
    overflow: "hidden",
    boxShadow: "0 1px 3px 0 rgba(0, 0, 0, 0.1)",
    marginBottom: "16px",
  },
  tableHeaderStyle: {
    backgroundColor: "#f8fafc",
    padding: "12px",
    textAlign: "left",
    fontWeight: "600",
    color: "#374151",
    borderBottom: "1px solid #e5e7eb",
  },
  tableCellStyle: {
    padding: "12px",
    borderBottom: "1px solid #e5e7eb",
    color: "#6b7280",
  },
  CellStyle: {
    padding: "12px",
    borderBottom: "1px solid #e5e7eb",
    backgroundColor: "#0000",
    fontWeight: "600",
    color: "#92400e",
  },

  tableStyle: {
    width: "100%",
    borderCollapse: "collapse",
  },
  tableHeaderStyle: {
    borderBottom: "1px solid #e5e7eb",
    textAlign: "left",
    padding: "8px 12px",
    background: "#f9fafb",
    color: "#374151",
    fontWeight: "bold",
  },
  CellStyle: {
    borderBottom: "1px solid #e5e7eb",
    padding: "8px 12px",
    fontSize: "0.95rem",
    color: "#111827",
  },
  controlsBar: {
    display: "flex",
    gap: "12px",
    alignItems: "center",
    marginBottom: "16px",
    flexWrap: "wrap",
  },
  input: {
    padding: "8px 10px",
    border: "1px solid #d1d5db",
    borderRadius: "6px",
    minWidth: "240px",
  },
  select: {
    padding: "8px 10px",
    border: "1px solid #d1d5db",
    borderRadius: "6px",
  },
  paginationBar: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginTop: "12px",
  },
  button: {
    padding: "6px 10px",
    border: "1px solid #d1d5db",
    borderRadius: "6px",
    background: "#fff",
    cursor: "pointer",
  },
  button_alarmoff: {
    border: "none",
    background: "transparent",
    padding: 0,
    cursor: "pointer",
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
  },

  iconContainerStyle: {
    position: "relative",
    display: "inline-block",
    cursor: "pointer",
    marginLeft: "6px",
    width: "20px",
    height: "20px",
    borderRadius: "50%",
    backgroundColor: "#59CBE8",
    color: "#fff",
    fontWeight: "bold",
    fontSize: "14px",
    textAlign: "center",
    lineHeight: "20px",
  },
};

const stripAccents = (s = "") =>
  s.normalize("NFD").replace(/[\u0300-\u036f]/g, "");

const TablaAlertas = (props) => {
  const [searchTerm, setSearchTerm] = useState("");
  const [searchField, setSearchField] = useState("all");
  const [bandeja, setFetchBandeja] = useState(props.bandeja);
  const [apagadas, setApagadas] = useState(props.apagadas);
  const [explicaciones, setExplicaciones] = useState(props.explicacion);
  const [ayuda, setAyuda] = useState(props.ayuda);
  const [pageSize, setPageSize] = useState(10);
  const [page, setPage] = useState(1);
  const [alerta, setSelectedAlerta] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [mensaje, setMensaje] = useState("");
  const [useApagadas, setUseApagadas] = useState(false);
  const [showPopover, setShowPopover] = useState(false);
  const handleIconClick = () => setShowPopover((prev) => !prev);
  const [showGuide, setShowGuide] = useState(false);
  const modalRef = useRef(null);
  const [showTooltip, setShowTooltip] = useState(false);
  const handleMouseClick = () => {
    setShowTooltip((prev) => !prev);
  };

  const tooltipStyle = {
    position: "absolute",
    bottom: "-45px",
    left: "50%",
    transform: "translateX(-50%)",
    backgroundColor: "#333",
    color: "#fff",
    padding: "6px",
    borderRadius: "4px",
    width: "220px",
    fontSize: "12px",
    opacity: showTooltip ? 1 : 0,
    visibility: showTooltip ? "visible" : "hidden",
    transition: "opacity 0.3s",
    zIndex: 10,
  };

  const modalDialogStyle = {
    position: "fixed",
    top: "50%",
    left: "50%",
    transform: "translate(-50%, -50%)",
    background: "#333",
    color: "#fff",
    width: "min(800px, 92vw)", // ← más ancho para textos grandes
    borderRadius: "10px",
    boxShadow: "0 12px 32px rgba(0,0,0,0.5)",
    zIndex: 1000,
    padding: "14px",
  };

  const iconContainerStyle = {
    position: "relative",
    display: "inline-block",
    cursor: "pointer",
    marginLeft: "6px",
    width: "22px",
    height: "22px",
    borderRadius: "50%",
    backgroundColor: "#59CBE8",
    color: "#fff",
    fontWeight: "bold",
    fontSize: "14px",
    textAlign: "center",
    lineHeight: "22px",
    boxShadow: "0 0 0 2px rgba(89, 203, 232, 0.2)",
  };

  const innerTdStyle = {
    padding: "10px",
    border: "1px solid #555",
    verticalAlign: "top",
    fontSize: "13px",
  };

  const innerThStyle = {
    backgroundColor: "#1f1f1f",
    color: "#fff",
    textAlign: "left",
    padding: "10px",
    border: "1px solid #555",
    fontWeight: 600,
    fontSize: "13px",
  };
  const DescargaButtonStyle = {
    backgroundColor: "#9063CD",
    color: "white",
    border: "none",
    borderRadius: "8px",
    padding: "8px 16px",
    fontSize: "1rem",
    fontWeight: "600",
    cursor: "pointer",
    display: "flex",
    alignItems: "center",
    gap: "8px",
    transition: "all 0.3s ease",
    boxShadow: "0 4px 6px -1px rgba(0, 0, 0, 0.1)",
  };
  const DownloadIcon = () => (
    <svg
      width="16"
      height="16"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
    >
      {/* Flecha de descarga */}
      <line x1="12" y1="5" x2="12" y2="15" />
      <polyline points="8 11 12 15 16 11" />

      {/* Barra inferior */}
      <rect x="5" y="17" width="14" height="2" rx="1" />
    </svg>
  );
  const popoverStyle = (visible) => ({
    position: "absolute",
    bottom: "-8px",
    left: "50%",
    transform: "translate(-50%, 100%)",
    backgroundColor: "#333",
    color: "#fff",
    padding: "12px",
    borderRadius: "8px",
    width: "min(720px, 90vw)",
    fontSize: "12px",
    boxShadow: "0 8px 24px rgba(0,0,0,0.3)",
    opacity: visible ? 1 : 0,
    visibility: visible ? "visible" : "hidden",
    transition: "opacity 0.2s ease-in-out, visibility 0.2s",
    zIndex: 20,
  });

  const innerTableStyle = {
    width: "100%",
    borderCollapse: "collapse",
    backgroundColor: "#2b2b2b",
    borderRadius: "6px",
    overflow: "hidden",
  };

  const leftGroupStyle = {
    display: "flex",
    alignItems: "center",
    gap: "8px",
    flexWrap: "wrap",
  };

  const guideButtonStyle = {
    display: "inline-flex",
    alignItems: "center",
    gap: "6px",
    backgroundColor: "#59CBE8",
    color: "#1b1b1b",
    border: "none",
    borderRadius: "6px",
    padding: "6px 10px",
    cursor: "pointer",
    fontWeight: 600,
    marginLeft: "auto",
  };
  const modalBackdropStyle = {
    position: "fixed",
    inset: 0,
    background: "rgba(0,0,0,0.35)",
    zIndex: 999,
    display: showGuide ? "block" : "none",
  };

  const modalHeaderStyle = {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    marginBottom: "8px",
  };

  const closeButtonStyle = {
    background: "transparent",
    border: "none",
    color: "#fff",
    fontSize: "20px",
    cursor: "pointer",
    lineHeight: 1,
  };

  useEffect(() => {
    const onKeyDown = (e) => {
      if (e.key === "Escape") setShowPopover(false);
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, []);

  const iconRef = useRef(null);
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (!iconRef.current) return;
      if (!iconRef.current.contains(e.target)) {
        setShowPopover(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);
  const sourceData = useMemo(
    () => (useApagadas ? apagadas : bandeja) ?? [],
    [useApagadas, bandeja, apagadas],
  );

  const filtered = useMemo(() => {
    const term = stripAccents(searchTerm.trim().toLowerCase());
    return (sourceData ?? [])
      .filter((d) => (useApagadas ? true : d?.estado === 1))
      .filter((d) => {
        if (!term) return true;

        const candidates = {
          idevaluacion: stripAccents(
            String(d?.idevaluacion ?? "").toLowerCase(),
          ),
          tipo_alerta: stripAccents(String(d?.tipo_alerta ?? "").toLowerCase()),
          nombre: stripAccents(String(d?.nombre ?? "").toLowerCase()),
        };

        if (
          searchField === "idevaluacion" ||
          searchField === "tipo_alerta" ||
          searchField === "nombre"
        ) {
          return candidates[searchField].includes(term);
        }
        return Object.values(candidates).some((v) => v.includes(term));
      });
  }, [sourceData, searchTerm, searchField, stripAccents]);

  const sorted = useMemo(
    () => ordenarPorOrigenIderoYTipo(filtered),
    [filtered],
  );
  useEffect(() => {
    setPage(1);
  }, [searchTerm, searchField, pageSize]);

  const totalItems = sorted.length;
  const totalPages = Math.max(1, Math.ceil(totalItems / pageSize));
  const startIndex = (page - 1) * pageSize;
  const pagedData = useMemo(
    () => sorted.slice(startIndex, startIndex + pageSize),
    [sorted, startIndex, pageSize],
  );

  const handlePrev = () => setPage((p) => Math.max(1, p - 1));
  const handleNext = () => setPage((p) => Math.min(totalPages, p + 1));

  const showingFrom = totalItems === 0 ? 0 : startIndex + 1;
  const showingTo = Math.min(startIndex + pageSize, totalItems);

  useEffect(() => {
    const total = sorted.length;
    const maxPage = Math.max(1, Math.ceil(total / pageSize));
    if (page > maxPage) {
      setPage(maxPage);
    }
  }, [sorted, page, pageSize]);

  const openModal = (alerta) => {
    setSelectedAlerta(alerta);
    setShowModal(true);
  };
  const getId = (item) => item.idalerta ?? item.idalertas;
  const handleSubmitJustificacion = async (payload) => {
    const body = {
      idalerta: payload.idalerta ?? payload.idalertas,
      justificacion: payload.justificacion,
      idanalistaresponsable: payload.idanalistaresponsable,
    };

    let apagadaItem;
    setFetchBandeja((prev) => {
      return prev.map((item) => {
        if (getId(item) === body.idalerta) {
          apagadaItem = {
            ...item,
            estado: 0,
            justificacion: body.justificacion,
          };
          return apagadaItem;
        }
        return item;
      });
    });
    if (apagadaItem) {
      setApagadas((prev) => [...prev, apagadaItem]);
    }
    try {
      const resp = await fetch(
        `${
          process.env.REACT_APP_API_URL
        }evaluacion/nuevaevaluacion/bandeja/${localStorage.getItem(
          "idusuario",
        )}/${localStorage.getItem("idcompania")}/`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Accept: "application/json",
            // Authorization: "Bearer " + token,
          },
          body: JSON.stringify(body),
        },
      );

      if (resp.ok) {
        const data = await resp.json();

        setMensaje(data.detail || "Cambio exitoso");
        setTimeout(() => setMensaje(""), 3000);
      }
      if (!resp.ok) {
        let detail = "";
        try {
          const errJson = await resp.json();
          detail = errJson?.detail || JSON.stringify(errJson);
        } catch {}
        throw new Error(`Error ${resp.status} ${detail}`);
      }
    } catch (err) {
      console.error("Error apagando alerta:", err);
      setMensaje("Error al actualizar");
      setTimeout(() => setMensaje(""), 3000);
      setFetchBandeja((prev) =>
        prev.map((item) =>
          getId(item) === body.idalerta ? { ...item, estado: 1 } : item,
        ),
      );
      setApagadas((prev) =>
        prev.filter((item) => getId(item) !== body.idalerta),
      );
    }
  };

  const OPCIONES_PREDEFINIDAS = ["Realizado", "Falsa alerta"];

  function MyVerticalModalAlerta({ show, alerta, onClose, onSubmit }) {
    const [justificacion, setJustificacion] = useState("");
    const [motivo, setMotivo] = useState("");
    const MAX = 150;
    const counterId = "justificacion-counter";

    const tipoColor = useMemo(() => {
      const t = alerta?.tipo_alerta ?? "";
      if (t === "Vulnerabilidades explotadas") return "danger";
      if (t === "Vulnerabilidades con Exploit conocido") return "warning";
      return "secondary";
    }, [alerta?.tipo_alerta]);
    const prefijo = motivo ? `${motivo}. ` : "";
    const justificacionCompuesta = `${prefijo}${justificacion}`;
    const maxLengthLibre = Math.max(0, MAX - prefijo.length);
    const remaining = MAX - justificacionCompuesta.length;
    const isValid =
      justificacionCompuesta.trim().length > 0 &&
      justificacionCompuesta.length <= MAX;
    useEffect(() => {
      if (show) {
        setJustificacion("");
        setMotivo("");
      }
    }, [show, alerta]);
    return (
      <Modal
        show={show}
        onHide={onClose}
        size="xl"
        centered
        aria-labelledby="modal-gestionar-alerta"
      >
        <Modal.Header closeButton>
          <Modal.Title
            id="modal-gestionar-alerta"
            style={{ display: "flex", alignItems: "center", gap: 12 }}
          >
            Gestionar alerta -
            <Badge style={{ fontSize: "1.5rem" }}>{alerta?.tipo_alerta}</Badge>
          </Modal.Title>
        </Modal.Header>

        <Modal.Body>
          <Row className="mb-3" xs={1} md={2}>
            <Col>
              <Card>
                <Card.Header>Resumen</Card.Header>
                <ListGroup variant="flush">
                  <ListGroup.Item>
                    <strong>ID Alerta:</strong> {alerta?.idalertas}
                  </ListGroup.Item>
                  <ListGroup.Item>
                    <strong>ID Evaluación:</strong> {alerta?.idevaluacion}
                  </ListGroup.Item>
                  <ListGroup.Item>
                    <strong>ID Aplicativo:</strong> {alerta?.idactivo}
                  </ListGroup.Item>
                  <ListGroup.Item>
                    <strong>Aplicativo:</strong> {alerta?.nombre}
                  </ListGroup.Item>
                  <ListGroup.Item
                    title={alerta?.elementos}
                    style={{
                      overflow: "visible",
                    }}
                  >
                    <strong>Elementos:</strong> {alerta?.elementos}{" "}
                    <span style={iconContainerStyle} onClick={handleMouseClick}>
                      ?
                      <span style={tooltipStyle}>
                        {explicaciones[alerta?.tipo_alerta]}
                      </span>
                    </span>
                  </ListGroup.Item>
                </ListGroup>
              </Card>
            </Col>

            <Col>
              <Card>
                <Card.Header>
                  Estado de completitud de la evaluación
                </Card.Header>
                <Card.Body>
                  <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
                    <BoolChip label="Control" value={alerta?.control} />
                    <BoolChip
                      label="Vulnerabilidad"
                      value={alerta?.vulnerabilidad}
                    />
                    <BoolChip
                      label="Infraestructura"
                      value={alerta?.infraestructura}
                    />
                    <EfectosChip value={alerta?.efectos} />
                  </div>
                  <div
                    style={{
                      marginTop: 12,
                      color: "#6B7280",
                      fontSize: "0.85rem",
                    }}
                  >
                    <strong>Analista:</strong>{" "}
                    {alerta?.analista?.trim() || "Sin responsable"}
                  </div>
                </Card.Body>
              </Card>
            </Col>
          </Row>
          <Card className="mb-2">
            <Card.Header>Conclusión</Card.Header>
            <Card.Body>
              <Form.Group controlId="justificacion-motivo" className="mb-3">
                <Form.Label>Opciones predefinidas (Opcional)</Form.Label>
                <div
                  className="d-flex flex-wrap align-items-center gap-3 ms-3"
                  style={{ textAlign: "left" }}
                >
                  {"  "}
                  {OPCIONES_PREDEFINIDAS.map((op) => (
                    <div
                      key={op}
                      className="d-flex align-items-center me-3"
                      style={{
                        whiteSpace: "nowrap",
                        flex: "0 0 auto",
                        marginRight: "1rem",
                      }}
                    >
                      <Form.Check
                        type="radio"
                        name="motivo-radio"
                        id={`motivo-${op.replace(/\s+/g, "-").toLowerCase()}`}
                        label={op}
                        checked={motivo === op}
                        onChange={() => setMotivo(op)}
                      />
                    </div>
                  ))}

                  {motivo ? (
                    <Button
                      variant="secondary"
                      size="sm"
                      onClick={() => setMotivo("")}
                      className="ms-2"
                    >
                      Quitar selección
                    </Button>
                  ) : null}
                </div>
              </Form.Group>
              <Form.Group controlId="justificacion">
                <Form.Label>
                  Describe la acción tomada. Este campo es opcional si
                  seleccionaste una opción predefinida; de lo contrario,
                  justifica desde cero. (Máx. {MAX} caracteres)
                </Form.Label>
                <Form.Control
                  as="textarea"
                  rows={4}
                  placeholder="Escribe tu justificación…"
                  value={justificacion}
                  maxLength={maxLengthLibre}
                  onChange={(e) => setJustificacion(e.target.value)}
                  aria-describedby={counterId}
                />
                <div
                  id={counterId}
                  style={{
                    textAlign: "right",
                    marginTop: 6,
                    color: remaining < 0 ? "#EF4444" : "#6B7280",
                  }}
                >
                  {remaining >= 0
                    ? `${remaining} caracteres restantes`
                    : `Te pasaste por ${Math.abs(remaining)} caracteres`}
                </div>
              </Form.Group>
            </Card.Body>
          </Card>
          <div
            style={{
              paddingTop: 12,
              display: "flex",
              gap: 8,
              justifyContent: "flex-end",
            }}
          >
            <Button variant="secondary" onClick={onClose}>
              Cancelar
            </Button>
            <Button
              className="botonPositivo3"
              variant="primary"
              disabled={!isValid}
              onClick={(e) => {
                e.preventDefault();
                const payload = {
                  idalerta: alerta?.idalertas,
                  justificacion: justificacionCompuesta.trim(),
                  idanalistaresponsable: localStorage.getItem("idusuario"),
                };
                onSubmit?.(payload);
                onClose?.();
              }}
              title={
                !isValid
                  ? `Agrega una justificación válida (1–${MAX} caracteres)`
                  : "Gestionar alerta"
              }
            >
              Gestionar alerta
            </Button>
          </div>
        </Modal.Body>
      </Modal>
    );
  }

  const normalize = (s, stripAccents) =>
    stripAccents
      ? stripAccents(String(s).trim().toLowerCase())
      : String(s).trim().toLowerCase();

  const tipoKeyOf = (d) => d?.tipo ?? d?.tipo_alerta ?? d?.tipoAlerta ?? "";

  const tipoExpMap = useMemo(() => {
    const map = new Map();
    for (const [k, v] of Object.entries(explicaciones)) {
      const key = normalize(k, stripAccents);
      map.set(key, Array.isArray(v) ? v : [v]);
    }
    return map;
  }, [stripAccents]);

  const uniqueTipos = useMemo(() => {
    const seen = new Set();
    const ordered = [];
    (pagedData ?? []).forEach((d) => {
      const rawTipo = tipoKeyOf(d);
      if (!rawTipo) return;
      const key = normalize(rawTipo, stripAccents);
      if (!seen.has(key)) {
        seen.add(key);
        ordered.push({ display: rawTipo, key });
      }
    });
    return ordered;
  }, [pagedData, stripAccents]);

  const handleOnExport = (bandeja, apagadas) => {
    const newBook = XLSX.utils.book_new();

    const sheet1 = XLSX.utils.json_to_sheet(bandeja || []);
    const sheet2 = XLSX.utils.json_to_sheet(apagadas || []);
    const date = new Date();
    const formattedDate =
      String(date.getDate()).padStart(2, "0") +
      "_" +
      String(date.getMonth() + 1).padStart(2, "0") +
      "_" +
      date.getFullYear();

    XLSX.utils.book_append_sheet(newBook, sheet1, "Alertas Activas");
    XLSX.utils.book_append_sheet(newBook, sheet2, "Alertas Gestionadas");
    XLSX.writeFile(newBook, "InformeAlertas" + formattedDate + ".xlsx");
  };

  const handleExportClick = async () => {
     try {
      if (ordenarPorOrigenIderoYTipo(sourceData ?? []).filter((d) => d?.estado === 1) || ordenarPorOrigenIderoYTipo(sourceData ?? []).filter((d) => d?.estado !== 1)) {
        handleOnExport(ordenarPorOrigenIderoYTipo(sourceData ?? []).filter((d) => d?.estado === 1), ordenarPorOrigenIderoYTipo(sourceData ?? []).filter((d) => d?.estado !== 1));
      } else {
      }
    } catch (error) {}
  };
  return (
    <>
      <div style={styles.controlsBar}>
        <input
          style={styles?.input}
          type="text"
          placeholder="Buscar"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
        />
        <select
          style={styles?.select}
          value={searchField}
          onChange={(e) => setSearchField(e.target.value)}
          title="Campo de búsqueda"
        >
          <option value="all">Filtrar por campo</option>
          <option value="idevaluacion">ID Evaluación (idevaluacion)</option>
          <option value="tipo_alerta">Tipo de alerta</option>
          <option value="nombre">Aplicativo (nombre)</option>
        </select>
        <label
          style={{
            display: "inline-flex",
            alignItems: "center",
            gap: 4,
            fontSize: "18px",
          }}
        >
          <input
            type="checkbox"
            checked={useApagadas}
            onChange={(e) => setUseApagadas(e.target.checked)}
            style={{
              width: "20px",
              height: "20px",
              transform: "scale(1)",
              marginRight: "4 px",
            }}
          />
          Mostrar alertas gestionadas
        </label>
        <button
          style={{
            ...DescargaButtonStyle,
            marginLeft: "20px", // opcional para separación
          }}
          onClick={handleExportClick}
          onMouseEnter={(e) => {
            e.target.style.backgroundColor = "#7A4FB8"; // tono más oscuro
            e.target.style.transform = "scale(1.05)"; // efecto de agrandar
          }}
          onMouseLeave={(e) => {
            e.target.style.backgroundColor = "#9063CD"; // vuelve al original
            e.target.style.transform = "scale(1)"; // tamaño normal
          }}
        >
          <DownloadIcon />
          Descargar Alertas
        </button>
        <button
          type="button"
          style={guideButtonStyle}
          onClick={() => setShowGuide(true)}
          title="Abrir guía de acciones"
          aria-haspopup="dialog"
          aria-expanded={showGuide}
        >
          <span aria-hidden="true">📘</span>
          Ayuda
        </button>
      </div>

      <div style={modalBackdropStyle}>
        <div
          role="dialog"
          aria-modal="true"
          style={modalDialogStyle}
          ref={modalRef}
        >
          <div style={modalHeaderStyle}>
            <strong>Guía de acciones recomendadas</strong>
            <button
              onClick={() => setShowGuide(false)}
              style={closeButtonStyle}
              aria-label="Cerrar"
              title="Cerrar"
            >
              ×
            </button>
          </div>
          {uniqueTipos.length > 0 ? (
            <table style={innerTableStyle}>
              <thead>
                <tr>
                  <th style={innerThStyle}>Tipo</th>
                  <th style={innerThStyle}>Acción recomendada</th>
                </tr>
              </thead>
              <tbody>
                {uniqueTipos.map(({ display, key }) => {
                  const accion = ayuda[display];
                  return (
                    <tr key={key}>
                      <td style={innerTdStyle}>{display}</td>
                      <td style={innerTdStyle}>
                        {accion ? accion : <em>Sin acción recomendada</em>}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          ) : (
            <div style={{ fontSize: "13px" }}>
              No se detectaron tipos en la página actual.
            </div>
          )}

          {/* Pie opcional con acción de cierre */}
          <div
            style={{
              marginTop: "10px",
              display: "flex",
              justifyContent: "flex-end",
            }}
          >
            <button
              onClick={() => setShowGuide(false)}
              style={{
                backgroundColor: "#59CBE8",
                color: "#1b1b1b",
                border: "none",
                borderRadius: "4px",
                padding: "6px 10px",
                cursor: "pointer",
                fontWeight: 600,
              }}
            >
              Entendido
            </button>
          </div>
        </div>
      </div>

      <div style={{ marginBottom: "24px" }}>
        <table style={styles.tableStyle}>
          <thead>
            <tr>
              <th
                style={{
                  ...styles.tableHeaderStyle,
                  width: colWidths.idevaluacion,
                }}
              >
                ID Eval.
              </th>
              <th
                style={{
                  ...styles.tableHeaderStyle,
                  width: colWidths.tipoAlerta,
                }}
              >
                Alerta
              </th>
              <th
                style={{
                  ...styles.tableHeaderStyle,
                  width: colWidths.analista,
                }}
              >
                Analista
              </th>
              <th
                style={{
                  ...styles.tableHeaderStyle,
                  width: colWidths.idActivo,
                }}
              >
                ID Aplicativo
              </th>
              <th
                style={{ ...styles.tableHeaderStyle, width: colWidths.nombre }}
              >
                Aplicativo
              </th>

              <th style={styles.tableHeaderStyle}>
                Detalle{" "}
                <span
                  ref={iconRef}
                  style={iconContainerStyle}
                  onClick={handleIconClick}
                  role="button"
                  tabIndex={0}
                  aria-haspopup="dialog"
                  aria-expanded={showPopover}
                  aria-controls="popover-frecuencia-limitada"
                  onKeyDown={(e) => {
                    if (e.key === "Enter" || e.key === " ") {
                      e.preventDefault();
                      handleIconClick();
                    }
                  }}
                  title="Más información"
                >
                  ?
                  <div
                    id="popover-frecuencia-limitada"
                    style={popoverStyle(showPopover)}
                    role="dialog"
                    aria-modal="false"
                  >
                    {uniqueTipos.length > 0 ? (
                      <table style={innerTableStyle}>
                        <thead>
                          <tr>
                            <th style={innerThStyle}>Tipo</th>
                            <th style={innerThStyle}>Explicación</th>
                          </tr>
                        </thead>
                        <tbody>
                          {uniqueTipos.map(({ display, key }) => {
                            const explicaciones = tipoExpMap.get(key);
                            return (
                              <tr key={key}>
                                <td style={innerTdStyle}>{display}</td>
                                <td style={innerTdStyle}>
                                  {explicaciones && explicaciones.length > 0 ? (
                                    explicaciones.length === 1 ? (
                                      explicaciones[0]
                                    ) : (
                                      <ul
                                        style={{
                                          margin: 0,
                                          paddingLeft: "18px",
                                        }}
                                      >
                                        {explicaciones.map((exp) => (
                                          <li key={exp}>{exp}</li>
                                        ))}
                                      </ul>
                                    )
                                  ) : (
                                    <em>Sin explicación disponible</em>
                                  )}
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    ) : (
                      <div style={{ fontSize: "13px" }}>
                        No se encontraron tipos en la página actual.
                      </div>
                    )}
                  </div>
                </span>
              </th>

              <th
                style={{ ...styles.tableHeaderStyle, width: colWidths.control }}
              >
                Control
              </th>
              <th
                style={{
                  ...styles.tableHeaderStyle,
                  width: colWidths.vulnerabilidad,
                }}
              >
                Vulnerabilidad
              </th>
              <th
                style={{ ...styles.tableHeaderStyle, width: colWidths.efectos }}
              >
                Efectos
              </th>
              <th
                style={{
                  ...styles.tableHeaderStyle,
                  width: colWidths.infraestructura,
                }}
              >
                Infraestructura
              </th>
              <th
                style={{
                  ...styles.tableHeaderStyle,
                  width: colWidths.acciones,
                  textAlign: "center",
                }}
              ></th>
            </tr>
          </thead>
          <tbody>
            {pagedData.length === 0 ? (
              <tr>
                <td style={styles.CellStyle} colSpan={11}>
                  No hay resultados para los filtros actuales.
                </td>
              </tr>
            ) : (
              pagedData.map((dato) => (
                <tr key={dato.idalertas}>
                  <td
                    style={{ ...styles.CellStyle }}
                    title={String(dato.idevaluacion ?? dato.idero)}
                  >
                    {dato.idevaluacion ?? dato.idero}
                  </td>
                  <td style={{ ...styles.CellStyle }} title={dato.tipo_alerta}>
                    {dato.tipo_alerta}
                  </td>
                  <td
                    style={{ ...styles.CellStyle }}
                    title={
                      dato.analista && dato.analista.trim() !== ""
                        ? dato.analista
                        : "Sin responsable"
                    }
                  >
                    {dato.analista && dato.analista.trim() !== ""
                      ? dato.analista
                      : "Sin responsable"}
                  </td>
                  <td
                    style={{ ...styles.CellStyle }}
                    title={String(dato.idactivo)}
                  >
                    {dato.idactivo}
                  </td>
                  <td style={{ ...styles.CellStyle }} title={dato.nombre}>
                    {dato.nombre}
                  </td>
                  <td style={{ ...styles.CellStyle }} title={dato.elementos}>
                    {dato.elementos}
                  </td>

                  <td
                    style={{ ...styles.CellStyle }}
                    title={String(dato.control ?? "")}
                  >
                    {renderBoolIcon(dato.control)}
                  </td>

                  <td
                    style={{ ...styles.CellStyle }}
                    title={String(dato.vulnerabilidad ?? "")}
                  >
                    {renderBoolIcon(dato.vulnerabilidad)}
                  </td>

                  <td
                    style={{ ...styles.CellStyle }}
                    title={String(dato.efectos ?? "")}
                  >
                    {renderPorcentajeFijo(dato.efectos)}
                  </td>

                  <td
                    style={{ ...styles.CellStyle }}
                    title={String(dato.infraestructura ?? "")}
                  >
                    {renderBoolIcon(dato.infraestructura)}
                  </td>

                  <td
                    style={{
                      ...styles.CellStyle,
                      textAlign: "center",
                      padding: "8px",
                    }}
                  >
                    {!useApagadas && (
                      <button
                        onClick={() => openModal(dato)}
                        title="Gestionar Alerta"
                        style={styles.button_alarmoff}
                      >
                        <AssignmentTurnedInIcon />
                      </button>
                    )}
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>

        {/* Paginación */}
        <div style={styles.paginationBar}>
          <div>
            Mostrando <strong>{showingFrom}</strong>–
            <strong>{showingTo}</strong> de <strong>{totalItems}</strong>
          </div>
          <div style={{ display: "flex", gap: "8px", alignItems: "center" }}>
            <select
              style={styles.select}
              value={pageSize}
              onChange={(e) => setPageSize(Number(e.target.value))}
              title="Elementos por página"
            >
              <option value={10}>10 por página</option>
              <option value={20}>20 por página</option>
              <option value={30}>30 por página</option>
            </select>
            <button
              style={styles.button}
              onClick={handlePrev}
              disabled={page <= 1}
            >
              ◀︎ Anterior
            </button>
            <span>
              Página <strong>{page}</strong> de <strong>{totalPages}</strong>
            </span>
            <button
              style={styles.button}
              onClick={handleNext}
              disabled={page >= totalPages}
            >
              Siguiente ▶︎
            </button>
          </div>
        </div>
      </div>

      <MyVerticalModalAlerta
        show={showModal}
        alerta={alerta}
        onClose={() => setShowModal(false)}
        onSubmit={handleSubmitJustificacion}
      />
      {mensaje && (
        <div
          style={{
            position: "fixed",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            backgroundColor: "#4caf50",
            color: "#fff",
            padding: "20px 40px",
            borderRadius: "8px",
            boxShadow: "0 4px 10px rgba(0,0,0,0.3)",
            fontSize: "18px",
            textAlign: "center",
            zIndex: 9999,
          }}
        >
          {mensaje}
        </div>
      )}
    </>
  );
};
export default TablaAlertas;
